import { useState } from 'react'
import { EndpointHint, Link } from '../../../modules/core/index.js'
import { Sparkline } from '../charts/Sparkline.js'
import { BAD, MARINE } from '../charts/chartColors.js'
import { DIRECTION_COLOR, asOfLabel, directionOf, sortByMovement, withoutMonth } from '../insightsData.js'
import { HEADLINE_LABELS, TABLE_HEADLINES, formatHeadline, monthLabel } from '../labels.js'
import type { HeadlineKey, InsightsPortfolio } from '../types.js'
import { MovementBadge } from './MovementBadge.js'
import { ShowMore } from './ShowMore.js'

const TILE_KEYS: readonly HeadlineKey[] = ['score', 'goldPct', 'callOutsPer100', 'fillPct', 'hppdWorked', 'retentionPct']
const GRID_KEYS: readonly HeadlineKey[] = ['score', 'callOutsPer100', 'fillPct', 'hppdWorked']
const ROWS_SHOWN = 12
/** The least a sparkline's vertical scale spans, so a wobble of a tenth never reads as a swing. */
const MIN_RANGE: Record<HeadlineKey, number> = { score: 5, goldPct: 5, callOutsPer100: 2, fillPct: 5, hppdWorked: 0.25, retentionPct: 5, headcount: 5 }

/**
 * The portfolio (plan section 6.1): six tiles with the whole portfolio's figure, its movement and twelve months in a
 * stroke; every facility ranked by the movement of the figure chosen, the one sliding most first; and a grid of small
 * multiples, one facility each, on the figure chosen. A row opens the facility's Dashboard.
 */
export function PortfolioScreen({ portfolio, facilityHref }: { portfolio: InsightsPortfolio; facilityHref: (facilityId: string) => string }) {
  const [sortKey, setSortKey] = useState<HeadlineKey>('score')
  const [gridKey, setGridKey] = useState<HeadlineKey>('score')
  const [showAllRows, setShowAllRows] = useState(false)
  const rows = sortByMovement(portfolio.facilities, sortKey)
  const uncounted = withoutMonth(rows)
  const latest = portfolio.totals.latest
  const span = portfolio.periods.length > 0 ? `${monthLabel(portfolio.periods[0]!)} to ${monthLabel(portfolio.periods[portfolio.periods.length - 1]!)}` : ''

  return (
    <div className="flex flex-col gap-6">
      <section aria-label="Portfolio summary" className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {TILE_KEYS.map((key) => (
          <div key={key} className="flex min-w-0 flex-col gap-1.5 rounded-lg border border-marine/15 bg-white px-4 py-3">
            <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{HEADLINE_LABELS[key]}</div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black tracking-tight text-ink">{formatHeadline(key, portfolio.totals.figures[key])}</span>
              <MovementBadge metric={key} movement={portfolio.totals.movement[key]} />
              {asOfLabel(portfolio.totals, key, monthLabel) && <span className="text-[10px] text-ink-mid">{asOfLabel(portfolio.totals, key, monthLabel)}</span>}
            </div>
            <Sparkline series={portfolio.totals.series[key]} minRange={MIN_RANGE[key]} label={`${HEADLINE_LABELS[key]}, the portfolio, ${span}`} />
            <div className="text-[10px] text-ink-mid">{latest ? `${monthLabel(latest, 'long')} · ${span}` : 'No month counted yet'}</div>
          </div>
        ))}
      </section>

      <section className="overflow-hidden rounded-lg border border-marine/15 bg-white">
        <div className="flex flex-wrap items-center gap-3 border-b border-marine/15 px-4 py-3">
          <h2 className="flex-1 text-sm font-black text-ink">
            Facilities by movement <EndpointHint className="ml-2">GET /insights/portfolio</EndpointHint>
          </h2>
          <label className="flex items-center gap-2 text-[11px] text-ink-mid">
            Sorted by the change in
            <select value={sortKey} onChange={(event) => setSortKey(event.target.value as HeadlineKey)} className="rounded border border-marine/30 bg-white px-2 py-1 text-xs text-ink">
              {TABLE_HEADLINES.map((key) => (
                <option key={key} value={key}>
                  {HEADLINE_LABELS[key]}
                </option>
              ))}
            </select>
          </label>
          <span className="text-[11px] text-ink-mid">The facility sliding most is first · a row opens its Dashboard</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-xs">
            <thead>
              <tr className="text-left text-[10px] font-bold uppercase tracking-wide text-ink-mid">
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">Facility</th>
                {TABLE_HEADLINES.map((key) => (
                  <th key={key} className="px-3 py-2">
                    {HEADLINE_LABELS[key]}
                  </th>
                ))}
                <th className="px-3 py-2 text-right">Since the month before</th>
              </tr>
            </thead>
            <tbody>
              {(showAllRows ? rows : rows.slice(0, ROWS_SHOWN)).map((row, index) => {
                const direction = directionOf(sortKey, row.movement[sortKey], 0.05)
                return (
                  <tr key={row.facilityId} className="border-t border-marine/10 hover:bg-marine-lt/30">
                    <td className="px-3 py-2 text-ink-mid">{index + 1}</td>
                    <td className="px-3 py-2">
                      <Link to={facilityHref(row.facilityId)} className="font-bold text-marine hover:text-marine-dk">
                        {row.name}
                      </Link>
                      <div className="text-[10px] text-ink-mid">{row.latest ? `${row.figures.headcount ?? '—'} workers · ${monthLabel(row.latest, 'long')}` : 'No month counted yet'}</div>
                    </td>
                    {TABLE_HEADLINES.map((key) => (
                      <td key={key} className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="min-w-[2.5rem] font-bold text-ink">
                            {formatHeadline(key, row.figures[key])}
                            {asOfLabel(row, key, monthLabel) && <span className="ml-1 text-[10px] font-normal text-ink-mid">{asOfLabel(row, key, monthLabel)}</span>}
                          </span>
                          <div className="w-[90px]">
                            <Sparkline series={row.series[key]} minRange={MIN_RANGE[key]} color={key === sortKey && direction === 'down' ? BAD : MARINE} label={`${row.name} ${HEADLINE_LABELS[key]} by month`} />
                          </div>
                        </div>
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right">
                      <MovementBadge metric={sortKey} movement={row.movement[sortKey]} />
                    </td>
                  </tr>
                )
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={TABLE_HEADLINES.length + 3} className="px-3 py-6 text-center text-ink-mid">
                    No facilities to show.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {(rows.length > ROWS_SHOWN || uncounted > 0) && (
          <div className="flex flex-wrap items-center gap-3 border-t border-marine/10 px-4 py-2 text-[11px]">
            {rows.length > ROWS_SHOWN && (
              <button type="button" onClick={() => setShowAllRows((current) => !current)} className="font-bold text-marine hover:text-marine-dk">
                {showAllRows ? `Show the first ${ROWS_SHOWN}` : `Show all ${rows.length} facilities`}
              </button>
            )}
            {uncounted > 0 && (
              <span className="text-ink-mid">
                {uncounted === rows.length ? 'No facility has a month counted yet.' : `${uncounted} ${uncounted === 1 ? 'facility has' : 'facilities have'} no month counted yet and ${uncounted === 1 ? 'sits' : 'sit'} last.`}
              </span>
            )}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="flex-1 text-sm font-black text-ink">Every facility at a glance</h2>
          <div role="group" aria-label="Figure" className="flex gap-1 rounded-lg bg-silver-lt p-0.5">
            {GRID_KEYS.map((key) => (
              <button key={key} type="button" aria-pressed={gridKey === key} onClick={() => setGridKey(key)} className={`rounded-md px-3 py-1.5 text-[11px] font-bold ${gridKey === key ? 'bg-white text-marine shadow-sm' : 'text-ink-mid'}`}>
                {HEADLINE_LABELS[key]}
              </button>
            ))}
          </div>
        </div>
        <ShowMore items={sortByMovement(portfolio.facilities, gridKey)} shown={ROWS_SHOWN} noun="facilities" render={(tiles) => (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          {tiles.map((row) => {
            const direction = directionOf(gridKey, row.movement[gridKey], 0.05)
            return (
              <Link key={row.facilityId} to={facilityHref(row.facilityId)} className="flex min-w-0 flex-col gap-2 rounded-lg border border-marine/15 bg-white px-3 py-3 text-inherit hover:border-marine/40" style={{ boxShadow: `inset 4px 0 0 ${direction ? DIRECTION_COLOR[direction] : '#C9D4DA'}` }}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-xs font-bold text-ink">{row.name}</span>
                  <MovementBadge metric={gridKey} movement={row.movement[gridKey]} />
                </div>
                <Sparkline series={row.series[gridKey]} minRange={MIN_RANGE[gridKey]} color={direction === 'down' ? BAD : MARINE} label={`${row.name} ${HEADLINE_LABELS[gridKey]} by month`} className="h-12" />
                <div className="flex justify-between text-[10px] text-ink-mid">
                  <span>{portfolio.periods[0] ? monthLabel(portfolio.periods[0]) : ''}</span>
                  <span className="font-bold text-ink">
                    {formatHeadline(gridKey, row.figures[gridKey])} {asOfLabel(row, gridKey, monthLabel) || 'now'}
                  </span>
                  <span>{row.latest ? monthLabel(row.latest) : ''}</span>
                </div>
              </Link>
            )
          })}
        </div>
        )} />
      </section>
    </div>
  )
}
